import React, { Fragment, useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Table,
  Button,
  Form,
  InputNumber,
  Input,
  Select,
  message,
  Spin,
  Tag,
  Collapse,
  Checkbox,
  Space,
  Tooltip,
  Image,
  List,
  Popconfirm,
} from "antd";
import { useSearchParams, useNavigate } from "react-router-dom";
import OpMenu from "../../PatientsOP/OpMenu";
import {
  getBillingDetailsApi,
  recordPaymentApi,
} from "../../../services/billing.services.js";
import exportIcon from "../../../assets/receptionistIcons/exportIcon.svg";
import { paymentKeys } from "../receptionKeys";
import { capitalizeFirstLetter, formatToINR } from "../../../helpers/utility";
import {
  createCashAmountRule,
  syncCashAmountValidation,
  validateCashPaymentAmount,
} from "../../../helpers/paymentValidation";
import inputStyles from "../../../styles/Custom/Input.module.css";
import btnStyles from "../../../styles/Custom/Button.module.css";
import PackagesScreen from "./PackagesScreen";
import { useContext } from "react";
import { UserContext } from "../../../context/UserContext";
import ServicesOpTabs from "./OPServices/ServicesOpTabs";
import PatientGalleryOP from "./Gallery/PatientGalleryOP";
import SummaryOP from "./SummaryOP.js";
import OpSummaryEmptyState from "./OpSummaryEmptyState";
import { getOpHistoryAPI } from "../../../services/physio.services";
import { shareBillApi } from "../../../services/receptionist.services";
import { DeleteOutlined } from "@ant-design/icons";
import DeletePaymentModal from "../DeletePaymentModal";
import CommonPopOver from "../../PatientsIP/ReceptionistIp/CommonPopOver";
import ActionIconButton from "../../ui/ActionIconButton";
import CustomLayout from "../../Layout/CustomLayout";
import OpBillingSummaryCards from "./OpBillingSummaryCards";
import "./receptionistOpContent.css";
import "./BillingPage.css";
import opStyles from "./ReceptionistOp.module.css";
const priceTagList = [
  { name: "₹", id: false },
  { name: "%", id: true },
];

const { Text } = Typography;
const { Panel } = Collapse;

const BillingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const admissionId = searchParams.get("admission");
  const patientId = searchParams.get("patient_id");
  const [activeTab, setActiveTab] = useState(
    searchParams.get("type") || "billing"
  );
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);
  const [currentEmr, setCurrentEmr] = useState(null);
  const [emrHistory, setEmrHistory] = useState([]);
  const [form] = Form.useForm();
  const enteredAmount = Form.useWatch(["payment", "amount"], form);
  const enteredDiscount = Form.useWatch("discount", form);

  const { userDetails } = useContext(UserContext);
  const facilityIdFromUser =
    userDetails?.organizations?.[0]?.facilities?.[0]?.facility_id;
  const [discount, setDiscount] = useState(0);
  const [partialPayment, setPartialPayment] = useState(false);
  const [amountToPay, setAmountToPay] = useState(0);
  const [doubleDiscount, setDoubleDiscount] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printButton, setPrintButton] = useState(false);
  const [currentDiscount, setCurrentDiscount] = useState(0);
  const [isPercent, setIsPercent] = useState(false);
  const isFirstPayment = billingData?.payments_history?.length === 0;
  const [editData, setEditData] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [paymentMode, setPaymentMode] = useState("cash");
  const watchedPaymentMode = Form.useWatch(["payment", "mode"], form);
  const activePaymentMode = watchedPaymentMode ?? paymentMode;

  useEffect(() => {
    syncCashAmountValidation(form, activePaymentMode, ["payment", "amount"]);
  }, [activePaymentMode, form]);

  const serviceTypeMap = {
    sports: "Sports EMR",
    ortho: "Ortho EMR",
    neuro: "Neuro EMR",
    cardio: "Cardiopulmonary EMR",
  };
  const refreshBillingData = async () => {
    try {
      const res = await getBillingDetailsApi(admissionId);
      const data = res.data;

      const updatedBilling = {
        patient_details: billingData.patient_details,
        services_summary: data.service_details?.services?.map((s, index) => ({
          id: index + 1,
          service_name: s.service,
          price: s.price,
          count: s.count,
          total: s.total_price,
          type: s.type,
        })),
        payments_history: data.payment_details?.payments?.map((p) => ({
          id: p.id,
          date: new Date(p.created_at).toLocaleDateString(),
          amount_paid: p.amount,
          payment_mode: p.mode,
          payment_id: p.id,
          bill_id: p.bill_id,
          status: p.status,
          ...p,
        })),
        bill_amount: data.payment_details?.total_amount || 0,
        balance: data.payment_details?.total_due_amount || 0,
        package_name: data.package?.name || "N/A",
        payment_details: data.payment_details,
      };

      setBillingData(updatedBilling);
      setAmountToPay(updatedBilling.balance);
    } catch (err) {
      console.error("Failed to refresh billing data:", err);
      message.error("Failed to refresh billing data");
    }
  };
  const getDoubleDiscountAmount = () => {
    const discountValue = form.getFieldValue("discount") || 0;
    const isPercent = form.getFieldValue("is_total_percent");
    if (Number(discountValue) > 0) {
      if (isPercent) {
        return ((Number(discountValue) * bill_amount) / 100).toFixed(2);
      } else {
        return Number(discountValue).toFixed(2);
      }
    }
    return 0;
  };

  const handleTabChange = async (key) => {
    setActiveTab(key);
    setSearchParams({
      admission: admissionId,
      patient_id: patientId,
      type: key,
    });
    await refreshBillingData();
  };

  useEffect(() => {
    if (admissionId) {
      setLoading(true);
      Promise.all([
        getBillingDetailsApi(admissionId),
        getOpHistoryAPI(patientId, admissionId),
      ])
        .then(([billingRes, historyRes]) => {
          const data = billingRes.data;

          const patient = data.admission?.patient || {};
          const patientDetails = {
            first_name: patient.first_name || "",
            last_name: patient.last_name || "",
            age: patient.age_by_dob || "",
            gender: patient.gender || "",
            op_id: data.admission?.op_id || "",
            facility_id: data.admission?.facility_id || "",
          };

          const services_summary = data.service_details?.services?.map(
            (s, index) => ({
              id: index + 1,
              service_name: s.service,
              price: s.price,
              date: "",
              total: s.total_price,
              count: s.count,
              type: s.type,
            })
          );

          const payments_history = data.payment_details?.payments?.map((p) => ({
            id: p.id,
            date: new Date(p.created_at).toLocaleDateString(),
            amount_paid: p.amount,
            payment_mode:
              p.mode === "others" ? p.other_mode || "Others" : p.mode,
            payment_id: p.id,
            bill_id: p.bill_id,
            status: p.status,
            ...p,
          }));

          const totalAmount = data.payment_details?.total_amount || 0;
          const totalDue = data.payment_details?.total_due_amount || 0;

          setBillingData({
            patient_details: patientDetails,
            services_summary,
            payments_history,
            bill_amount: totalAmount,
            balance: totalDue,
            package_name: data.package?.name || "N/A",
            payment_details: data.payment_details,
          });
          const historyData = historyRes?.data?.data || {};

          const latestEmr = historyData?.emr_record || null;
          const pastEmrs = historyData?.past_emrs || [];
          const allEmrs = [
            ...(latestEmr ? [latestEmr] : []),
            ...pastEmrs.filter((emr) => emr.id !== latestEmr?.id),
          ];

          setEmrHistory(allEmrs);
          setCurrentEmr(allEmrs[0] ?? null);

          setAmountToPay(totalDue);
        })
        .catch((err) => {
          console.error(err);
          message.error("Failed to load billing data.");
        })
        .finally(() => setLoading(false));
    }
  }, [admissionId, activeTab]);
  useEffect(() => {
    if (billingData?.payment_details) {
      setAmountToPay(billingData.payment_details.total_due_amount || 0);
      form.setFieldsValue({
        payment: { amount: billingData.payment_details.total_due_amount || 0 },
      });
    }
  }, [billingData]);
  useEffect(() => {
    if (!partialPayment) {
      const due = Number(dueAmount || 0);
      const discount = Number(form.getFieldValue("discount") || 0);

      const payableAmount = Math.max(due - discount, 0);

      form.setFieldsValue({
        payment: {
          amount: payableAmount,
        },
      });
    }
  }, [enteredDiscount, partialPayment]);

  const handleRecordPayment = async (values) => {
    try {
      const discountValue = Number(values.discount || 0);
      let finalAmount = 0;

      if (!partialPayment) {
        finalAmount = Number(dueAmount) - discountValue;
        if (finalAmount < 0) finalAmount = 0;
      } else {
        finalAmount = Number(values.payment?.amount || 0);
      }

      if (finalAmount < 0) {
        message.error("Invalid payment amount");
        return;
      }

      const isPercent = false;
      let finalDiscount = 0;
      if (billingData?.payment_details?.total_due_amount <= 0) {
        message.error("No amount to be paid.");
        return;
      }
      if (discountValue) {
        if (isPercent) {
          finalDiscount = (Number(discountValue) * bill_amount) / 100;
        } else {
          finalDiscount = Number(discountValue);
        }
      }
      const enteredAmt = Number(values.payment?.amount || 0);
      const enteredDisc = Number(values.discount || 0);

      if (enteredAmt + enteredDisc > Number(dueAmount)) {
        message.error("Payment cannot be greater than balance");
        return;
      }

      const cashError = validateCashPaymentAmount(
        finalAmount,
        values.payment?.mode
      );
      if (cashError) {
        message.error(cashError);
        return;
      }

      const payload = {
        admission_id: admissionId,
        amount: finalAmount,
        mode: values.payment?.mode,
        discount: Number(values.discount || 0),
        partial_payment: partialPayment,
        transaction_number: values.transaction_number,
        remarks: values.remarks,
      };

      const billId = billingData.payment_details?.bill_id;
      if (!billId) {
        message.error("Bill ID not found.");
        return;
      }

      setIsSaving(true);
      await recordPaymentApi(billId, payload);
      message.success(`Payment of ₹${finalAmount} recorded successfully!`);
      await refreshBillingData();
      const res = await getBillingDetailsApi(admissionId);
      const data = res.data;

      setBillingData({
        patient_details: billingData.patient_details,
        services_summary: data.service_details?.services?.map((s, index) => ({
          id: index + 1,
          service_name: s.service,
          price: s.price,
          date: "",
          total: s.total_price,
          count: s.count,
          type: s.type,
        })),
        payments_history: data.payment_details?.payments?.map((p) => ({
          id: p.id,
          date: new Date(p.created_at).toLocaleDateString(),
          amount_paid: p.amount,
          payment_mode: p.mode,
          payment_id: p.id,
          bill_id: p.bill_id,
          status: p.status,
          ...p,
        })),
        bill_amount: data.payment_details?.total_amount || 0,
        balance: data.payment_details?.total_due_amount || 0,
        package_name: data.package?.name || "N/A",
        payment_details: data.payment_details,
      });

      setAmountToPay(data.payment_details?.total_due_amount || 0);

      form.resetFields();
      setDiscount(0);
      setPartialPayment(false);
      setDoubleDiscount(false);
      setCurrentDiscount(0);
    } catch (err) {
      console.error(err);
      message.error("Failed to record payment.");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrintBill = async () => {
    if (!billingData?.payment_details?.bill_id) {
      message.error("Bill ID not found for printing.");
      return;
    }

    setIsPrinting(true);

    try {
      const billId = billingData.payment_details.bill_id;
      const res = await shareBillApi(billId, "type=bill&share=print");
      const htmlContent = res?.data?.data;

      if (!htmlContent) {
        message.error("Failed to retrieve bill.");
        return;
      }

      const printWindow = window.open("", "_blank");
      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } catch (err) {
      console.error("Print error:", err);
      message.error("Failed to print bill. Please try again.");
    } finally {
      setIsPrinting(false);
    }
  };
  const getFinalAmountPayable = () => {
    if (!doubleDiscount) return dueAmount;

    let discountAmount = 0;

    if (Number(currentDiscount) > 0) {
      discountAmount = isPercent
        ? (Number(currentDiscount) * bill_amount) / 100
        : Number(currentDiscount);
    }

    const finalAmount = dueAmount - discountAmount;
    return finalAmount >= 0 ? finalAmount.toFixed(2) : 0;
  };

  const downLoadBill = async (bill_id, payment_id) => {
    try {
      const query = `type=payment&payment_id=${payment_id}&share=download`;

      const res = await shareBillApi(bill_id, query);
      const s3Link = res?.data?.data;

      if (s3Link && s3Link.endsWith(".pdf")) {
        const link = document.createElement("a");
        link.href = s3Link;
        link.download = `Bill_${bill_id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else if (s3Link) {
        window.open(s3Link, "_blank");
      } else {
        message.error("Failed to retrieve the PDF link.");
      }
    } catch (err) {
      console.error("Download error:", err);
      message.error("Error downloading the bill. Please try again.");
    }
  };

  const getLiveDueAmount = () => {
    const amount = Number(enteredAmount || 0);
    const discount = Number(enteredDiscount || 0);

    const newDue = dueAmount - amount - discount;

    return newDue > 0 ? newDue.toFixed(2) : "0.00";
  };

  const {
    patient_details,
    services_summary,
    payments_history,
    bill_amount,
    package_name,
  } = billingData || {};
  const packageServices = services_summary?.filter(
    (item) => item.type === "package"
  );

  const normalServices = services_summary?.filter(
    (item) => item.type === "service"
  );

  const packageTotal = packageServices?.reduce(
    (sum, item) => sum + Number(item.total || 0),
    0
  );

  const servicesTotal = normalServices?.reduce(
    (sum, item) => sum + Number(item.total || 0),
    0
  );

  const paymentTotal = payments_history?.reduce(
    (sum, item) => sum + Number(item.amount_paid || 0),
    0
  );

  const paidAmount = billingData?.payment_details?.total_paid_amount || 0;
  const dueAmount = billingData?.payment_details?.total_due_amount || 0;
  const renderEmrBars = () => {
    if (emrHistory.length <= 1) return null;

    const orgPrimaryColor =
      userDetails?.activeOrg?.color || "var(--ant-primary-color, #6941C6)";

    return (
      <div
        className={opStyles.summaryEmrCards}
        style={{ "--org-primary": orgPrimaryColor }}
      >
        {emrHistory.map((emr) => (
          <Card
            key={emr.id}
            size="small"
            hoverable
            className={`${opStyles.summaryEmrCard} ${
              currentEmr?.id === emr.id ? opStyles.summaryEmrCardActive : ""
            }`}
            style={{
              minWidth: 250,
              padding: 6,
              border:
                currentEmr?.id === emr.id
                  ? undefined
                  : "1px solid #d9d9d9",
              cursor: "pointer",
              flex: "0 0 auto",
            }}
            onClick={() => setCurrentEmr(emr)}
          >
            <Typography.Text strong style={{ fontSize: 15 }}>
              {serviceTypeMap[emr.service_type] || emr.service_type || "N/A"}
            </Typography.Text>
            <br />
            <Space size={1} direction="vertical">
              <Typography.Text style={{ fontSize: 10, fontWeight: 600 }}>
                Created At:{" "}
                {emr.created_at
                  ? new Date(emr.created_at).toLocaleString()
                  : "N/A"}
              </Typography.Text>
            </Space>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <Fragment>
      <OpMenu
        patientData={patient_details || {}}
        navigate={navigate}
        params={{ type: activeTab }}
        onTabClick={({ key }) => handleTabChange(key)}
      />
      <div className="receptionist-op-content">
        {activeTab === "billing" && loading ? (
          <div style={{ textAlign: "center" }}>
            <Spin tip="Loading billing details..." />
          </div>
        ) : activeTab === "billing" && !billingData ? (
          <div>No billing data found.</div>
        ) : activeTab === "billing" && billingData ? (
          <>
            <OpBillingSummaryCards
              package_name={package_name}
              bill_amount={bill_amount}
              dueAmount={dueAmount}
            />

            <Row gutter={16} className={opStyles.billingColumns}>
              <Col xs={24} lg={12}>
                <div className={opStyles.billingTableScroll}>
                  <Table
                    title={() => (
                      <Row justify="space-between">
                        <Text strong>Package Billing</Text>
                        <Text strong>Sub Total: ₹ {packageTotal}</Text>
                      </Row>
                    )}
                    dataSource={packageServices}
                    columns={[
                      {
                        title: "Package Name",
                        dataIndex: "service_name",
                        key: "service_name",
                      },
                      { title: "Count", dataIndex: "count", key: "count" },
                      { title: "Total", dataIndex: "total", key: "total" },
                    ]}
                    pagination={false}
                    rowKey="id"
                    style={{ marginBottom: "20px" }}
                  />
                </div>

                <div className={opStyles.billingTableScroll}>
                  <Table
                    title={() => (
                      <Row justify="space-between">
                        <Text strong>Services Billing</Text>
                        <Text strong>Sub Total: ₹ {servicesTotal}</Text>
                      </Row>
                    )}
                    dataSource={normalServices}
                    columns={[
                      {
                        title: "Service Name",
                        dataIndex: "service_name",
                        key: "service_name",
                      },
                      { title: "Price", dataIndex: "price", key: "price" },
                      { title: "Count", dataIndex: "count", key: "count" },
                      { title: "Total", dataIndex: "total", key: "total" },
                    ]}
                    pagination={false}
                    rowKey="id"
                  />
                </div>
              </Col>

              <Col xs={24} lg={12}>
                <div className={opStyles.billingTableScroll}>
                  <Table
                    title={() => (
                      <Row justify="space-between">
                        <Text strong>Record Payment</Text>
                        <Tag color="blue">
                          {" "}
                          Total Amount Paid: {paymentTotal}
                        </Tag>
                      </Row>
                    )}
                    dataSource={payments_history}
                    columns={[
                      { title: "Date", dataIndex: "date", key: "date" },
                      {
                        title: "Amount Paid",
                        dataIndex: "amount_paid",
                        key: "amount_paid",
                      },
                      {
                        title: "Payment Mode",
                        dataIndex: "payment_mode",
                        key: "payment_mode",
                      },
                      {
                        title: "Status",
                        dataIndex: "status",
                        key: "status",
                        render: (value, record) => {
                          return (
                            <Space>
                              <Tag
                                color={value === "paid" ? "green" : "orange"}
                              >
                                {value === "delete_requested"
                                  ? "Delete Requested"
                                  : capitalizeFirstLetter(value)}
                              </Tag>
                              {record?.payment_delete_request_reason && (
                                <CommonPopOver record={record} />
                              )}
                            </Space>
                          );
                        },
                      },
                      {
                        title: "Action",
                        key: "action",
                        render: (_, record) => (
                          <Space>
                            <Tooltip title="Download Receipt">
                              <ActionIconButton
                                aria-label="Download Receipt"
                                onClick={() =>
                                  downLoadBill(
                                    record?.bill_id,
                                    record?.payment_id
                                  )
                                }
                              >
                                <Image
                                  src={exportIcon}
                                  alt="export"
                                  preview={false}
                                  style={{ width: 20, cursor: "pointer" }}
                                />
                              </ActionIconButton>
                            </Tooltip>
                            {record?.status !== "delete_requested" &&
                              !record?.payment_delete_rejected && (
                                <Popconfirm
                                  title="Sure to raise delete request?"
                                  onConfirm={() => {
                                    setEditData(record?.id);
                                    setOpenDeleteModal(true);
                                  }}
                                >
                                  <Button
                                    type="text"
                                    danger
                                    className="ui-action-icon"
                                    aria-label="Delete"
                                    icon={<DeleteOutlined />}
                                  />
                                </Popconfirm>
                              )}
                          </Space>
                        ),
                      },
                    ]}
                    pagination={false}
                    rowKey="id"
                  />
                </div>

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleRecordPayment}
                >
                  <Row gutter={[12, 12]} style={{ marginTop: "20px" }}>
                    <Col span={24}>
                      <Card
                        className={`bill-summary-mobile ${opStyles.billSummaryCard}`}
                        title={
                          <div
                            className="billSummaryCardHeader"
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span>Bill Summary</span>
                            <Button
                              loading={isPrinting}
                              type="primary"
                              onClick={handlePrintBill}
                              className={btnStyles.lgBtn}
                            >
                              {partialPayment
                                ? "Download Receipt"
                                : "Download Bill"}
                            </Button>
                          </div>
                        }
                      >
                        <List size="small" split={false}>
                          {!isFirstPayment && (
                            <List.Item>
                              <List.Item.Meta title="SubTotal (Including Taxes)" />
                              <div style={{ paddingRight: "150px" }}>
                                {formatToINR(
                                  billingData?.payment_details?.total_amount ||
                                    0
                                )}
                              </div>
                            </List.Item>
                          )}
                          {/* {!isFirstPayment && ( */}
                          <List.Item>
                            <List.Item.Meta title="Total Discount" />
                            <div style={{ paddingRight: "150px" }}>
                              {formatToINR(
                                billingData?.payment_details?.total_discount ||
                                  0
                              )}
                            </div>
                          </List.Item>
                          {/* )} */}
                          <List.Item>
                            <List.Item.Meta title="Total" />
                            <div style={{ paddingRight: "150px" }}>
                              {formatToINR(dueAmount)}
                            </div>
                          </List.Item>
                          {!isFirstPayment && (
                            <List.Item>
                              <List.Item.Meta title="Amount Paid till date" />
                              <div style={{ paddingRight: "150px" }}>
                                {formatToINR(paidAmount)}
                              </div>
                            </List.Item>
                          )}
                          <List.Item>
                            <List.Item.Meta
                              title={
                                partialPayment
                                  ? "Enter the Amount"
                                  : "Due Amount"
                              }
                            />
                            <Form.Item
                              name={["payment", "amount"]}
                              initialValue={dueAmount}
                              rules={[
                                {
                                  validator: (_, value) => {
                                    const amount = Number(value || 0);
                                    const discount = Number(
                                      form.getFieldValue("discount") || 0
                                    );

                                    if (amount + discount > Number(dueAmount)) {
                                      return Promise.reject(
                                        "Payment cannot be greater than balance"
                                      );
                                    }

                                    return Promise.resolve();
                                  },
                                },
                                createCashAmountRule(form, ["payment", "mode"]),
                              ]}
                              hasFeedback={false}
                            >
                              <InputNumber
                                suffix="₹"
                                min={0}
                                disabled={!partialPayment}
                                style={{ width: "100%" }}
                                className={inputStyles.mdNumberInput}
                              />
                            </Form.Item>
                          </List.Item>
                          <div
                            style={{
                              fontStyle: "italic",
                              color: "#686868",
                              textAlign: "right",
                              marginRight: "10px",
                            }}
                          >
                            Due Amount: ₹ {getLiveDueAmount()}
                          </div>
                          <div className="flex-between">
                            {" "}
                            <Form.Item name="partial_payment">
                              <Checkbox
                                checked={partialPayment}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setPartialPayment(checked);
                                  if (!checked) {
                                    form.setFieldsValue({
                                      payment: { amount: dueAmount },
                                    });
                                  }
                                }}
                              />{" "}
                              Partial Payment
                            </Form.Item>
                            <Form.Item name="double_discount">
                              <Checkbox
                                checked={doubleDiscount}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setDoubleDiscount(checked);
                                  if (!checked) {
                                    setCurrentDiscount(0);
                                    form.setFieldsValue({
                                      discount: undefined,
                                    });
                                  }
                                }}
                              >
                                {billingData?.payment_details
                                  ?.total_discount === 0 ||
                                billingData?.payment_details?.total_discount ===
                                  null
                                  ? "Discount"
                                  : "Double Discount"}
                              </Checkbox>
                            </Form.Item>
                          </div>
                          {doubleDiscount && (
                            <List.Item>
                              <List.Item.Meta title="Discount (In Rupees)" />

                              <Space.Compact>
                                <Form.Item
                                  name="discount"
                                  rules={[
                                    {
                                      validator: (_, value) => {
                                        const amount = Number(
                                          form.getFieldValue([
                                            "payment",
                                            "amount",
                                          ]) || 0
                                        );
                                        if (
                                          amount + Number(value || 0) >
                                          Number(dueAmount)
                                        ) {
                                          return Promise.reject(
                                            "Payment cannot be greater than balance"
                                          );
                                        }
                                        const percentage =
                                          form.getFieldValue(
                                            "is_total_percent"
                                          );
                                        if (value && value !== "") {
                                          if (
                                            /^[0-9]*\.?[0-9]{1,3}$/.test(value)
                                          ) {
                                            if (
                                              percentage === true &&
                                              Number(value) > 100
                                            ) {
                                              return Promise.reject(
                                                "Discount cannot be greater than 100%"
                                              );
                                            } else if (
                                              percentage === true &&
                                              (Number(value) * bill_amount) /
                                                100 >
                                                dueAmount
                                            ) {
                                              return Promise.reject(
                                                "Discount cannot be greater than payable amount"
                                              );
                                            } else if (
                                              Number(value) > Number(dueAmount)
                                            ) {
                                              return Promise.reject(
                                                "Discount cannot be greater than due amount"
                                              );
                                            }
                                            return Promise.resolve();
                                          } else {
                                            return Promise.reject(
                                              "Please enter a valid discount"
                                            );
                                          }
                                        }
                                        return Promise.resolve();
                                      },
                                    },
                                  ]}
                                  hasFeedback={false}
                                >
                                  <InputNumber
                                    style={{ width: "150px" }}
                                    min={0}
                                    className={inputStyles.mdNumberInput}
                                  />
                                </Form.Item>
                                {/* <Form.Item name="is_total_percent" style={{ width: "50px" }}>
                                          <Select
                                          className={inputStyles.mdSelect}
                                          defaultValue={false}
                                          onChange={() => {
                                            form.setFields([{ name: "discount", errors: [] }]);
                                          form.validateFields(["discount"]);
                                        }}
                                        >
                                          {priceTagList.map((data) => (
                                            <Select.Option key={data.id} value={data.id}>
                                              {data.name}
                                              </Select.Option>
                                            ))}
                                            </Select>
                                            </Form.Item> */}
                              </Space.Compact>
                              {/* {form.getFieldValue("discount") && form.getFieldValue("is_total_percent") && (
                                              <div style={{ marginTop: 8, fontWeight: 200 }}>
                                                Calculated Discount: ₹ {getDoubleDiscountAmount()}
                                                </div>
                                              )} */}
                            </List.Item>
                          )}
                          <List.Item>
                            <List.Item.Meta title="Payment Mode" />
                            <Form.Item
                              name={["payment", "mode"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Please select a payment mode",
                                },
                              ]}
                            >
                              <Select
                                style={{ width: "200px" }}
                                options={paymentKeys}
                                placeholder="Select Payment Mode"
                                className={inputStyles.mdSelect}
                                onChange={(val) => {
                                  setPaymentMode(val);
                                  syncCashAmountValidation(form, val, [
                                    "payment",
                                    "amount",
                                  ]);
                                }}
                              />
                            </Form.Item>
                          </List.Item>
                          {paymentMode === "others" && (
                            <List.Item>
                              <List.Item.Meta title="Other Mode" />
                              <Form.Item
                                name={["payment", "other_mode"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please specify the payment mode",
                                  },
                                ]}
                              >
                                <Input
                                  style={{ width: "300px" }}
                                  className={inputStyles.mdInput}
                                  placeholder="Enter other payment mode"
                                />
                              </Form.Item>
                            </List.Item>
                          )}
                          {paymentMode !== "cash" && paymentMode !== "others" && (
                            <List.Item>
                              <List.Item.Meta title="Transaction Number" />
                              <Form.Item name="transaction_number">
                                <Input
                                  style={{ width: "300px" }}
                                  min={0}
                                  className={inputStyles.mdInput}
                                />
                              </Form.Item>
                            </List.Item>
                          )}
                          <List.Item>
                            <List.Item.Meta title="Remarks" />
                            <Form.Item name="remarks">
                              <Input
                                style={{ width: "300px" }}
                                min={0}
                                className={inputStyles.mdInput}
                              />
                            </Form.Item>
                          </List.Item>
                        </List>

                        <div
                          className="flex-between"
                          style={{
                            justifyContent: "center",
                            marginTop: "10px",
                          }}
                        >
                          <Space>
                            <Button
                              loading={isSaving}
                              type="primary"
                              htmlType="submit"
                              className={btnStyles.lgBtn}
                            >
                              Generate Receipt
                            </Button>
                          </Space>
                        </div>
                      </Card>
                    </Col>
                  </Row>
                </Form>
              </Col>
            </Row>
          </>
        ) : activeTab === "packages" ? (
          <PackagesScreen admissionId={admissionId} />
        ) : activeTab === "services" ? (
          <ServicesOpTabs patientData={patient_details || {}} />
        ) : activeTab === "gallery" ? (
          <PatientGalleryOP patientData={billingData?.patient_details} />
        ) : activeTab === "summary" ? (
          <>
            {renderEmrBars()}
            {currentEmr?.id ? (
              <SummaryOP
                emrId={currentEmr.id}
                serviceType={currentEmr.service_type}
              />
            ) : (
              <OpSummaryEmptyState />
            )}
          </>
        ) : null}
        {openDeleteModal && (
          <DeletePaymentModal
            open={openDeleteModal}
            setOpen={setOpenDeleteModal}
            editData={editData}
            refresh={refreshBillingData}
          />
        )}
      </div>
    </Fragment>
  );
};

export default BillingPage;
